import { Flex, Table, ActionIcon, Tooltip, Group } from "@mantine/core";
import { IconTrash, IconEdit } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryApplications } from "hooks/events/useQueryApplications";
import { useDeleteApplication } from "hooks/events/useDeleteApplication";
import { useAppTranslation } from "hooks/useTranslation";
import { limitTextLength } from "utils";
import { useState } from "react";
import { YesNoDialog } from "components/YesNoDialog";

const perPage = 20;

// The queue lists Jotform submissions and agenda imports only (the filter
// lives in queryApplications). Facebook imports are not listed: the curators
// do not work them yet, and the newsroom desk will be their home.
export const Applications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  const { isLoading, isError, error, applications, count } =
    useQueryApplications({ page, perPage });

  const [opened, { open, close }] = useDisclosure(false);
  const [deletedId, setDeletedId] = useState<number | null>(null);
  const deleteApplicationMutation = useDeleteApplication(() => close());

  const deleteDialog = (id: number) => {
    setDeletedId(id);
    open();
  };
  const deleteApplicationConfirmed = () => {
    if (deletedId !== null) {
      deleteApplicationMutation.mutate({ id: deletedId });
    }
  };

  // Mutate-and-re-set: handing setSearchParams a fresh object literal instead
  // replaces the whole param set.
  const changePage = (page: number) => {
    searchParams.set("page", page.toString());
    setSearchParams(searchParams);
  };

  const navigateToEdit = (app: any) => {
    navigate(`/applications/${app.id}`, { state: { application: app } });
  };

  return (
    <Flex direction="column" justify="space-between" style={{ padding: 24 }}>
      {isError && (
        <span style={{ color: "red" }}>
          {t("common.error")}: {error?.message}
        </span>
      )}
      {isLoading ? (
        <span>{t("common.loading")}</span>
      ) : (
        <Table
          horizontalSpacing="md"
          verticalSpacing="xs"
          miw={700}
          highlightOnHover
          styles={{ td: { paddingTop: 5, paddingBottom: 5 } }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("events.event_title")}</Table.Th>
              <Table.Th>{t("events.starting_date")}</Table.Th>
              <Table.Th>{t("events.organizer")}</Table.Th>
              <Table.Th>{t("events.venue")}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {applications && applications.length > 0
              ? applications.map((app: any) => (
                  <Table.Tr
                    key={app.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigateToEdit(app)}
                  >
                    <Table.Td>
                      {limitTextLength
                        ? limitTextLength(app.event_title)
                        : app.event_title}
                    </Table.Td>
                    <Table.Td>
                      {app.starting_date
                        ? dayjs(app.starting_date).format("ddd, DD/MM/YYYY")
                        : "—"}
                    </Table.Td>
                    <Table.Td>
                      {limitTextLength
                        ? limitTextLength(app.organiser_name)
                        : app.organiser_name}
                    </Table.Td>
                    <Table.Td>
                      {limitTextLength
                        ? limitTextLength(app.event_venue_location)
                        : app.event_venue_location}
                    </Table.Td>
                    <Table.Td style={{ width: "120px" }}>
                      <Group gap={8}>
                        <Tooltip label={t("tooltips.edit")} openDelay={500}>
                          <ActionIcon
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToEdit(app);
                            }}
                          >
                            <IconEdit />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("tooltips.delete")} openDelay={500}>
                          <ActionIcon
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDialog(app.id);
                            }}
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              : null}
          </Table.Tbody>
        </Table>
      )}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "center" }}>
        {count && count > perPage ? (
          <div style={{ display: "flex", gap: 8 }}>
            {Array.from({ length: Math.ceil(count / perPage) }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  border:
                    page === i + 1 ? "2px solid #1971c2" : "1px solid #ccc",
                  background: page === i + 1 ? "#e7f5ff" : "#fff",
                  cursor: "pointer",
                  fontWeight: page === i + 1 ? 600 : 400,
                }}
                onClick={() => changePage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <YesNoDialog
        opened={opened}
        title={t("common.delete") + " " + t("navigation.applications")}
        question={t("events.delete_confirmation")}
        onClose={close}
        onYes={deleteApplicationConfirmed}
      />
    </Flex>
  );
};
